// AntAttachmentList — component MỚI quản lý danh sách file đính kèm
// inline trong AntBaseForm cho mọi voucher. ĐỘC LẬP với AttachmentPanel
// trong AntLeftSidebar.
//
// Props:
//   voucherType    — bắt buộc (vd 'OFDocument', 'AROrder')
//   voucherCode    — để link junction (text ưu tiên)
//   voucherId      — fallback nếu chưa có code
//   voucherModule  — module BE (mặc định derive theo prefix voucherType)
//   readOnly       — disable upload + delete khi true
//   height         — px (mặc định 220)
//   onChange       — function(count) — notify thay đổi attachments
//
// API endpoints:
//   GET  list:        /media/MEDDocumentVoucher/list  (filter voucher_type + voucher_code/id)
//   POST upload:      /media/MEDDocument/upload       (FormData files + voucher_type/code/id)
//   POST detach:      /media/MEDDocument/actionDetachFromVoucher
//     → BE tự refcount: nếu MEDDocument hết junction sau detach → hard delete + cascade S3
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import AntAPI from '../../utils/AntAPI';
import AntLocalization from '../../utils/AntLocalization';
import { AntSession } from '../../utils/AntContext';
import AntFileViewer from '../AntFileViewer';
import './index.scss';

function L(v) { return !v ? '' : (typeof v === 'string' ? v : AntLocalization.translateText(v)); }

function formatBytes(n) {
    var b = Number(n || 0);
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
    if (b < 1024 * 1024 * 1024) return (b / 1024 / 1024).toFixed(1) + ' MB';
    return (b / 1024 / 1024 / 1024).toFixed(2) + ' GB';
}

function deriveModule(voucherType) {
    if (!voucherType) return 'ar';
    var prefix = voucherType.slice(0, 2).toLowerCase();
    var map = { ar: 'ar', ap: 'ap', ca: 'ca', of: 'of', ic: 'inv', mf: 'mf', ai: 'ai', cr: 'crm', sa: 'saleChannel', sc: 'socialChannel', vr: 'vrm', hr: 'hrm', pm: 'pm', sp: 'spc' };
    return map[prefix] || 'ar';
}

function fileExt(name) {
    if (!name) return '';
    var dot = name.lastIndexOf('.');
    return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

function iconForType(type, ext) {
    var t = (type || '').toLowerCase();
    if (t === 'image' || ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'].indexOf(ext) >= 0) return 'image';
    if (t === 'pdf' || ext === 'pdf') return 'pdf';
    if (t === 'video' || ['mp4', 'mov', 'webm', 'mkv', 'avi'].indexOf(ext) >= 0) return 'video';
    if (t === 'audio' || ['mp3', 'wav', 'm4a', 'flac', 'ogg'].indexOf(ext) >= 0) return 'audio';
    if (['doc', 'docx', 'odt', 'rtf'].indexOf(ext) >= 0) return 'doc';
    if (['xls', 'xlsx', 'csv', 'ods'].indexOf(ext) >= 0) return 'xls';
    if (['ppt', 'pptx', 'odp'].indexOf(ext) >= 0) return 'ppt';
    if (['zip', 'rar', '7z', 'tar', 'gz'].indexOf(ext) >= 0) return 'zip';
    if (['txt', 'md', 'json', 'xml', 'log'].indexOf(ext) >= 0) return 'text';
    return 'file';
}

// SVG icon mặc định cho mỗi loại file (vector, có màu fill theo SCSS variant)
function FileIcon(props) {
    var kind = props.kind;
    var common = { width: 40, height: 40, viewBox: '0 0 48 48', fill: 'none' };
    if (kind === 'pdf') return (
        <svg {...common}><path d="M14 4h16l10 10v26a4 4 0 0 1-4 4H14a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z" fill="#fdecea" stroke="#e74c3c" strokeWidth="1.5"/><path d="M30 4v10h10" fill="#fff" stroke="#e74c3c" strokeWidth="1.5"/><text x="24" y="34" textAnchor="middle" fill="#e74c3c" fontSize="9" fontWeight="700">PDF</text></svg>
    );
    if (kind === 'doc') return (
        <svg {...common}><path d="M14 4h16l10 10v26a4 4 0 0 1-4 4H14a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z" fill="#ebf4fb" stroke="#2b6cb0" strokeWidth="1.5"/><path d="M30 4v10h10" fill="#fff" stroke="#2b6cb0" strokeWidth="1.5"/><text x="24" y="34" textAnchor="middle" fill="#2b6cb0" fontSize="9" fontWeight="700">DOC</text></svg>
    );
    if (kind === 'xls') return (
        <svg {...common}><path d="M14 4h16l10 10v26a4 4 0 0 1-4 4H14a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z" fill="#e6fffa" stroke="#2f855a" strokeWidth="1.5"/><path d="M30 4v10h10" fill="#fff" stroke="#2f855a" strokeWidth="1.5"/><text x="24" y="34" textAnchor="middle" fill="#2f855a" fontSize="9" fontWeight="700">XLS</text></svg>
    );
    if (kind === 'ppt') return (
        <svg {...common}><path d="M14 4h16l10 10v26a4 4 0 0 1-4 4H14a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z" fill="#fef5ec" stroke="#d35400" strokeWidth="1.5"/><path d="M30 4v10h10" fill="#fff" stroke="#d35400" strokeWidth="1.5"/><text x="24" y="34" textAnchor="middle" fill="#d35400" fontSize="9" fontWeight="700">PPT</text></svg>
    );
    if (kind === 'video') return (
        <svg {...common}><rect x="6" y="10" width="36" height="28" rx="3" fill="#f0eafb" stroke="#6b46c1" strokeWidth="1.5"/><path d="M21 19l9 5-9 5z" fill="#6b46c1"/></svg>
    );
    if (kind === 'audio') return (
        <svg {...common}><circle cx="24" cy="24" r="18" fill="#e6fffa" stroke="#0987a0" strokeWidth="1.5"/><path d="M19 17v14m10-14v14M16 22v4m16-4v4" stroke="#0987a0" strokeWidth="2" strokeLinecap="round"/></svg>
    );
    if (kind === 'zip') return (
        <svg {...common}><path d="M14 4h20l6 6v30a4 4 0 0 1-4 4H14a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z" fill="#edf2f7" stroke="#4a5568" strokeWidth="1.5"/><path d="M22 8h4v4h-4zm0 6h4v4h-4zm0 6h4v4h-4z" fill="#4a5568"/></svg>
    );
    if (kind === 'text') return (
        <svg {...common}><path d="M14 4h16l10 10v26a4 4 0 0 1-4 4H14a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z" fill="#f7fafc" stroke="#718096" strokeWidth="1.5"/><path d="M30 4v10h10" fill="#fff" stroke="#718096" strokeWidth="1.5"/><path d="M16 22h14M16 28h14M16 34h10" stroke="#718096" strokeWidth="1.5" strokeLinecap="round"/></svg>
    );
    return (
        <svg {...common}><path d="M14 4h16l10 10v26a4 4 0 0 1-4 4H14a4 4 0 0 1-4-4V8a4 4 0 0 1 4-4z" fill="#edf2f7" stroke="#718096" strokeWidth="1.5"/><path d="M30 4v10h10" fill="#fff" stroke="#718096" strokeWidth="1.5"/></svg>
    );
}

export default function AntAttachmentList(props) {
    var voucherType = props.voucherType;
    var voucherCode = props.voucherCode;
    var voucherId = props.voucherId;
    var voucherModule = props.voucherModule || deriveModule(voucherType);
    var readOnly = !!props.readOnly;
    var height = props.height || 220;

    var docAPI = useMemo(function () { return new AntAPI('media', 'MEDDocument'); }, []);
    var junctionAPI = useMemo(function () { return new AntAPI('media', 'MEDDocumentVoucher'); }, []);
    var session = AntSession ? AntSession() : null;

    var [files, setFiles] = useState([]);
    var [loading, setLoading] = useState(false);
    var [uploading, setUploading] = useState(false);
    var [progress, setProgress] = useState(0);
    var [viewerFile, setViewerFile] = useState(null);
    var [dragOver, setDragOver] = useState(false);
    var [error, setError] = useState(null);
    var fileInputRef = useRef(null);

    // ─── Load attachments ──────────────────────────────────────
    var load = useCallback(function () {
        if (!voucherType || (!voucherCode && !voucherId)) {
            setFiles([]);
            return;
        }
        setLoading(true);
        var body = { voucher_type: voucherType, pageSize: 100, pageIndex: 0 };
        if (voucherId) body.voucher_id = voucherId;
        else body.voucher_code = voucherCode;
        junctionAPI.post('/' + junctionAPI.routeURL + '/list', body, function (r) {
            var rows = (r && r.data && r.data.rows) || (r && r.rows) || [];
            var mapped = (Array.isArray(rows) ? rows : []).map(function (x) {
                return {
                    junction_id: x.id,
                    document_id: x.document_id,
                    document_code: x.document_code,
                    name: x.document_name,
                    type: x.document_type,
                    file_size: x.file_size || x.document_file_size || 0,
                    short_path: x.document_short_path || x.short_path,
                    full_path: x.document_full_path || x.full_path,
                    sub_type: x.document_sub_type || x.sub_type,
                    attached_at: x.attached_at,
                    source: x.source
                };
            });
            setFiles(mapped);
            setLoading(false);
            if (props.onChange) props.onChange(mapped.length);
        });
    }, [voucherType, voucherCode, voucherId, junctionAPI]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(function () { load(); }, [load]);

    // Socket sync — đồng bộ với AttachmentPanel (LeftSidebar) khi attach/detach
    // ở component khác cùng voucher.
    useEffect(function () {
        if (!session || !session.registerSocketListener) return;
        if (!voucherType || (!voucherCode && !voucherId)) return;
        var listenerKey = 'AntAttachmentList-' + voucherType + '-' + (voucherCode || voucherId);
        var handler = function (data) {
            if (!data) return false;
            if (data.event !== 'MEDDocumentVoucher-attached' && data.event !== 'MEDDocumentVoucher-detached') return false;
            if (data.voucher_type !== voucherType) return true;
            if (voucherCode && data.voucher_code && data.voucher_code !== voucherCode) return true;
            if (voucherId && data.voucher_id && String(data.voucher_id) !== String(voucherId)) return true;
            load();
            return true;
        };
        session.registerSocketListener(handler, listenerKey);
        return function () { session.clearSocketListener(listenerKey); };
    }, [session, voucherType, voucherCode, voucherId, load]);

    // ─── Upload ────────────────────────────────────────────────
    var doUpload = useCallback(function (selected) {
        if (!voucherType || (!voucherCode && !voucherId)) {
            setError(L({ vi: 'Vui lòng lưu phiếu trước khi đính kèm', en: 'Please save before attaching', fr: 'Enregistrez le document avant de joindre', es: 'Guarde el documento antes de adjuntar', id: 'Simpan dokumen sebelum melampirkan', ja: '添付する前に伝票を保存してください', zh: '请先保存单据再添加附件', ko: '첨부 전에 전표를 저장하세요', hi: 'संलग्न करने से पहले वाउचर सहेजें', ar: 'احفظ المستند قبل الإرفاق' }));
            return;
        }
        if (!selected || selected.length === 0) return;
        setError(null);
        setUploading(true);
        setProgress(0);
        var fd = new FormData();
        for (var i = 0; i < selected.length; i++) fd.append('files', selected[i]);
        fd.append('voucher_type', voucherType);
        if (voucherCode) fd.append('voucher_code', voucherCode);
        if (voucherId) fd.append('voucher_id', voucherId);
        docAPI.upload('/' + docAPI.routeURL + '/upload', fd, function (r) {
            setUploading(false);
            setProgress(0);
            if (r && r.error) {
                setError(typeof r.error === 'string' ? r.error : L({ vi: 'Tải lên thất bại', en: 'Upload failed', fr: 'Échec de l\'envoi', es: 'Error al subir', id: 'Gagal mengunggah', ja: 'アップロード失敗', zh: '上传失败', ko: '업로드 실패', hi: 'अपलोड विफल', ar: 'فشل الرفع' }));
                return;
            }
            load();
        }, function (pct) { setProgress(pct || 0); });
    }, [voucherType, voucherCode, voucherId, docAPI, load]);

    var handlePickFiles = useCallback(function (e) {
        var selected = e.target.files;
        doUpload(selected);
        e.target.value = '';
    }, [doUpload]);

    var openPicker = useCallback(function () {
        if (readOnly || uploading) return;
        if (fileInputRef.current) fileInputRef.current.click();
    }, [readOnly, uploading]);

    // ─── Drag-drop ─────────────────────────────────────────────
    var onDragOver = useCallback(function (e) {
        if (readOnly || uploading) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer.types && Array.from(e.dataTransfer.types).indexOf('Files') >= 0) {
            setDragOver(true);
        }
    }, [readOnly, uploading]);
    var onDragLeave = useCallback(function (e) {
        e.preventDefault(); e.stopPropagation(); setDragOver(false);
    }, []);
    var onDrop = useCallback(function (e) {
        e.preventDefault(); e.stopPropagation(); setDragOver(false);
        if (readOnly || uploading) return;
        var f = e.dataTransfer.files;
        if (f && f.length > 0) doUpload(f);
    }, [readOnly, uploading, doUpload]);

    // ─── Detach ───────────────────────────────────────────────
    // BE tự refcount: nếu sau detach MEDDocument hết junction → hard delete + cascade S3.
    var detach = useCallback(function (file) {
        if (readOnly) return;
        if (!window.confirm(L({ vi: 'Gỡ tệp khỏi phiếu?', en: 'Detach file from voucher?', fr: 'Détacher le fichier du document ?', es: '¿Quitar el archivo del documento?', id: 'Lepas file dari dokumen?', ja: '伝票からファイルを外しますか？', zh: '从单据中移除文件？', ko: '전표에서 파일을 분리할까요?', hi: 'फ़ाइल को वाउचर से हटाएँ?', ar: 'فصل الملف عن المستند؟' }))) return;
        docAPI.post('/' + docAPI.routeURL + '/actionDetachFromVoucher', {
            document_id: file.document_id,
            voucher_type: voucherType,
            voucher_code: voucherCode,
            voucher_id: voucherId
        }, function (r) {
            if (r && r.error) {
                window.alert(r.error);
                return;
            }
            load();
        });
    }, [voucherType, voucherCode, voucherId, docAPI, load, readOnly]);

    // ─── Preview ────────────────────────────────────────────────
    var preview = useCallback(function (file) {
        if (!file.full_path && !file.short_path) {
            window.alert(L({ vi: 'Tệp không có đường dẫn xem trước', en: 'File has no preview path', fr: 'Aucun lien d\'aperçu pour ce fichier', es: 'El archivo no tiene vista previa', id: 'File tidak punya tautan pratinjau', ja: 'プレビュー用のパスがありません', zh: '文件没有预览路径', ko: '미리보기 경로가 없습니다', hi: 'फ़ाइल का पूर्वावलोकन पथ नहीं है', ar: 'لا يوجد مسار معاينة للملف' }));
            return;
        }
        setViewerFile({
            name: file.name,
            url: file.full_path,
            type: file.sub_type || ''
        });
    }, []);

    // ─── Render ────────────────────────────────────────────────
    var hasFiles = files.length > 0;
    var notSavedYet = !voucherCode && !voucherId;
    var disabledUpload = readOnly || uploading;

    return (
        <div className={'ant-attachment-list' + (dragOver ? ' is-drag-over' : '')}
             style={{ minHeight: height }}
             onDragOver={onDragOver}
             onDragLeave={onDragLeave}
             onDrop={onDrop}>
            <div className="aal-header">
                <span className="aal-title">
                    {L({ vi: 'Tệp đính kèm', en: 'Attachments', fr: 'Pièces jointes', es: 'Adjuntos', id: 'Lampiran', ja: '添付ファイル', zh: '附件', ko: '첨부 파일', hi: 'संलग्नक', ar: 'المرفقات' })}
                    {hasFiles && <span className="aal-count">({files.length})</span>}
                </span>
                {!readOnly && (
                    <button type="button"
                            className="aal-upload-btn"
                            disabled={disabledUpload}
                            onClick={openPicker}
                            title={notSavedYet
                                ? L({ vi: 'Lưu phiếu trước khi đính kèm', en: 'Save voucher first', fr: 'Enregistrez d\'abord le document', es: 'Guarde primero el documento', id: 'Simpan dokumen dahulu', ja: '先に伝票を保存してください', zh: '请先保存单据', ko: '먼저 전표를 저장하세요', hi: 'पहले वाउचर सहेजें', ar: 'احفظ المستند أولًا' })
                                : ''}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 4v12m-5-7l5-5 5 5M5 21h14" />
                        </svg>
                        {uploading
                            ? (L({ vi: 'Đang tải', en: 'Uploading', fr: 'Envoi', es: 'Subiendo', id: 'Mengunggah', ja: '送信中', zh: '上传中', ko: '업로드 중', hi: 'अपलोड जारी', ar: 'جارٍ الرفع' }) + (progress ? ' ' + progress + '%' : '...'))
                            : L({ vi: 'Tải lên', en: 'Upload', fr: 'Téléverser', es: 'Subir', id: 'Unggah', ja: '添付', zh: '上传', ko: '업로드', hi: 'अपलोड', ar: 'رفع' })}
                    </button>
                )}
                <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handlePickFiles} />
            </div>

            {error && <div className="aal-error">{error}</div>}

            {loading && <div className="aal-empty">{L({ vi: 'Đang tải...', en: 'Loading...', fr: 'Chargement...', es: 'Cargando...', id: 'Memuat...', ja: '読み込み中...', zh: '加载中...', ko: '로딩 중...', hi: 'लोड हो रहा...', ar: 'تحميل...' })}</div>}

            {!loading && !hasFiles && (
                <div className="aal-empty">
                    {readOnly
                        ? L({ vi: 'Chưa có tệp đính kèm', en: 'No attachments', fr: 'Aucune pièce jointe', es: 'Sin adjuntos', id: 'Belum ada lampiran', ja: '添付ファイルなし', zh: '暂无附件', ko: '첨부 파일 없음', hi: 'कोई अनुलग्नक नहीं', ar: 'لا توجد مرفقات' })
                        : (notSavedYet
                            ? L({ vi: 'Lưu phiếu trước rồi mới tải lên hoặc kéo thả tệp được', en: 'Save voucher first to upload or drop files', fr: 'Enregistrez le document pour téléverser ou déposer des fichiers', es: 'Guarde el documento para subir o soltar archivos', id: 'Simpan dokumen dahulu untuk mengunggah atau menaruh file', ja: 'ファイルを添付するには先に伝票を保存してください', zh: '请先保存单据，再上传或拖放文件', ko: '파일을 업로드하거나 끌어 놓으려면 먼저 전표를 저장하세요', hi: 'फ़ाइल अपलोड या ड्रॉप करने से पहले वाउचर सहेजें', ar: 'احفظ المستند أولًا لرفع الملفات أو إفلاتها' })
                            : L({ vi: 'Kéo thả tệp vào đây hoặc nhấn "Tải lên"', en: 'Drag files here or click "Upload"', fr: 'Déposez des fichiers ici ou cliquez "Téléverser"', es: 'Arrastre archivos aquí o pulse "Subir"', id: 'Seret file ke sini atau klik "Unggah"', ja: 'ここにドラッグ、または"添付"をクリック', zh: '将文件拖到此处或点击"上传"', ko: '파일을 여기에 끌어 놓거나 "업로드" 클릭', hi: 'फ़ाइलें यहाँ खींचें या "अपलोड" दबाएँ', ar: 'اسحب الملفات هنا أو انقر "رفع"' }))}
                </div>
            )}

            {!loading && hasFiles && (
                <div className="aal-grid">
                    {files.map(function (f) {
                        var ext = fileExt(f.name);
                        var iconKind = iconForType(f.type, ext);
                        var isImg = iconKind === 'image' && f.full_path;
                        return (
                            <div key={f.junction_id || f.document_code}
                                 className={'aal-card' + (isImg ? ' aal-card--image' : '')}
                                 onClick={function (e) {
                                     if (e.target.closest && e.target.closest('.aal-delete')) return;
                                     preview(f);
                                 }}
                                 title={L({ vi: 'Xem trước', en: 'Preview', fr: 'Aperçu', es: 'Vista previa', id: 'Pratinjau', ja: 'プレビュー', zh: '预览', ko: '미리보기', hi: 'पूर्वावलोकन', ar: 'معاينة' })}>
                                <div className={'aal-icon aal-icon--' + iconKind}>
                                    {isImg
                                        ? <img src={f.full_path} alt={f.name} loading="lazy" />
                                        : <FileIcon kind={iconKind} />}
                                </div>
                                <div className="aal-body">
                                    <div className="aal-name" title={f.name}>{f.name}</div>
                                    <div className="aal-meta">{formatBytes(f.file_size)}</div>
                                </div>
                                {!readOnly && (
                                    <button type="button" className="aal-delete"
                                            onClick={function (e) { e.stopPropagation(); detach(f); }}
                                            title={L({ vi: 'Gỡ tệp', en: 'Detach', fr: 'Détacher', es: 'Quitar', id: 'Lepas', ja: '解除', zh: '移除', ko: '분리', hi: 'हटाएँ', ar: 'فصل' })}>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                                             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {dragOver && !readOnly && (
                <div className="aal-drop-overlay">
                    {L({ vi: 'Thả tệp để đính kèm', en: 'Drop to attach', fr: 'Déposez pour joindre', es: 'Suelte para adjuntar', id: 'Lepas untuk melampirkan', ja: 'ドロップして添付', zh: '松开以添加附件', ko: '놓아서 첨부', hi: 'संलग्न करने हेतु छोड़ें', ar: 'أفلت للإرفاق' })}
                </div>
            )}

            {viewerFile && (
                <AntFileViewer
                    visible
                    files={[viewerFile]}
                    initialIndex={0}
                    onClose={function () { setViewerFile(null); }}
                />
            )}
        </div>
    );
}
